import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Grid, IconButton, TextField, Typography, Switch, Paper,
  Divider, CircularProgress, alpha, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, Tooltip, Avatar,
} from "@mui/material";
import {
  DragDropContext, Droppable, Draggable, DropResult,
} from "@hello-pangea/dnd";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import PublishIcon from "@mui/icons-material/Publish";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SearchIcon from "@mui/icons-material/Search";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import ImageIcon from "@mui/icons-material/Image";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TvIcon from "@mui/icons-material/Tv";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TimerIcon from "@mui/icons-material/Timer";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import StorageIcon from "@mui/icons-material/Storage";
import EventIcon from "@mui/icons-material/Event";
import HistoryIcon from "@mui/icons-material/History";
import {
  useGetPlaylistQuery,
  useUpdatePlaylistMutation,
  useGetMediaQuery,
  useAddPlaylistItemMutation,
  useRemovePlaylistItemMutation,
  useReorderPlaylistItemsMutation,
} from "../../../Api/attoDeskApi";
import {
  IMedia, IPlaylist, IPlaylistItem,
} from "../../../Api/Interface/api.interface";
import PageLoader from "../../../Components/Loader/PageLoader";
import { useNotifier } from "../../../Core/Notifier";
import {
  NAVY, NAVY_DARK, GOLD, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";
import { useUploads } from "../../../Components/Uploads/UploadContext";

const DEFAULT_IMAGE_DURATION = 10;
const DURATION_PRESETS = [5, 10, 15, 20, 30, 60];

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return "0s";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const formatBytes = (b: number): string => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
};

const formatRelative = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Tiny presentation primitives                                              */
/* ──────────────────────────────────────────────────────────────────────── */

const StatBadge: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ReactElement;
  tint: string;
  bg: string;
}> = ({ label, value, icon, tint, bg }) => (
  <Paper elevation={0} sx={{
    p: 2.5, borderRadius: "16px", border: `1px solid ${BORDER}`,
    boxShadow: "0 2px 6px rgba(11,30,45,0.05)", height: "100%", bgcolor: "#fff",
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.25 }}>
      <Avatar sx={{ bgcolor: bg, color: tint, width: 38, height: 38 }}>
        {React.cloneElement(icon, { sx: { fontSize: 19 } })}
      </Avatar>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 700, color: TEXT_SECONDARY,
        textTransform: "uppercase", letterSpacing: "0.06em",
      }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{
      fontSize: 26, fontWeight: 800, color: NAVY, lineHeight: 1.1,
      letterSpacing: "-0.02em",
    }}>
      {value}
    </Typography>
  </Paper>
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{
    display: "flex", justifyContent: "space-between", alignItems: "center",
    py: 1.25, borderBottom: `1px solid ${BORDER}`,
    "&:last-of-type": { borderBottom: "none" },
  }}>
    <Typography sx={{ fontSize: 11, fontWeight: 600, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.04em" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
      {value}
    </Typography>
  </Box>
);

const SectionCard: React.FC<{
  icon: React.ReactElement;
  title: string;
  accent: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, accent, subtitle, right, children }) => (
  <Paper elevation={0} sx={{
    mb: 3, borderRadius: 3, border: `1px solid ${BORDER}`,
    overflow: "hidden", bgcolor: "#fff",
  }}>
    <Box sx={{
      background: `linear-gradient(135deg, ${alpha(accent, 0.04)} 0%, ${alpha(accent, 0.01)} 100%)`,
      borderBottom: `1px solid ${alpha(accent, 0.08)}`,
      px: 3, py: 2.5,
      display: "flex", alignItems: "center", gap: 1.5,
    }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: 2,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(accent, 0.06)} 100%)`,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 18, color: accent } })}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography fontSize={15} fontWeight={700} color={NAVY} letterSpacing="-0.01em">
          {title}
        </Typography>
        {subtitle && (
          <Typography fontSize={11} color="#94a3b8" mt={0.25}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {right}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

const TypeBadge: React.FC<{ type: "image" | "video" }> = ({ type }) => (
  <Chip
    size="small"
    icon={type === "image" ? <ImageIcon sx={{ fontSize: 11 }} /> : <OndemandVideoIcon sx={{ fontSize: 11 }} />}
    label={type === "image" ? "IMAGE" : "VIDEO"}
    sx={{
      height: 20, fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em",
      bgcolor: type === "image" ? "#dbeafe" : "#0f172a",
      color: type === "image" ? "#1e40af" : "#fff",
      border: "none",
      "& .MuiChip-icon": {
        color: type === "image" ? "#3b82f6" : "#cbd5e1",
        ml: 0.5,
      },
    }}
  />
);

const MediaThumb: React.FC<{ media: IMedia; size?: number; rounded?: number }> = ({
  media, size = 80, rounded = 8,
}) => {
  if (media.media_type === "image") {
    return (
      <Box component="img" src={media.url} alt=""
        sx={{
          width: size, height: size * 0.7, objectFit: "cover",
          borderRadius: `${rounded}px`, display: "block",
          bgcolor: "#f1f5f9", flexShrink: 0,
        }} />
    );
  }
  return (
    <Box sx={{
      position: "relative", width: size, height: size * 0.7, borderRadius: `${rounded}px`,
      background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff", flexShrink: 0, overflow: "hidden",
    }}>
      <PlayCircleFilledIcon sx={{ fontSize: size * 0.35, opacity: 0.95 }} />
    </Box>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Main page                                                                  */
/* ──────────────────────────────────────────────────────────────────────── */

const PlaylistEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const playlistId = Number(id);
  const { showMessage, showErrorMessage } = useNotifier();

  const { data, isLoading, refetch } = useGetPlaylistQuery(playlistId);
  const [updatePlaylist, { isLoading: isSaving }] = useUpdatePlaylistMutation();
  const [addItem] = useAddPlaylistItemMutation();
  const [removeItem] = useRemovePlaylistItemMutation();
  const [reorderItems, { isLoading: isReordering }] = useReorderPlaylistItemsMutation();
  const [publishing, setPublishing] = useState(false);

  // ── State
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [items, setItems] = useState<IPlaylistItem[]>([]);

  // ── Modals
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [durationFor, setDurationFor] = useState<IPlaylistItem | null>(null);

  const playlist = data?.data as unknown as IPlaylist | undefined;

  useEffect(() => {
    if (playlist) {
      setName(playlist.name);
      setDescription(playlist.description ?? "");
      setIsActive(playlist.is_active);
      setItems(playlist.items || []);
    }
  }, [playlist]);

  // ── Aggregates for summary cards
  const stats = useMemo(() => {
    const images = items.filter(i => i.media?.media_type === "image").length;
    const videos = items.filter(i => i.media?.media_type === "video").length;
    const imageDuration = items
      .filter(i => i.media?.media_type === "image")
      .reduce((s, i) => s + (i.duration || DEFAULT_IMAGE_DURATION), 0);
    return { images, videos, imageDuration };
  }, [items]);

  // ── Mutations
  const persistMeta = async (overrides?: Partial<{ name: string; description: string | null; is_active: boolean }>) => {
    return updatePlaylist({
      id: playlistId,
      body: {
        name: (overrides?.name ?? name).trim(),
        description: (overrides?.description !== undefined ? overrides.description : description.trim()) || null,
        is_active: overrides?.is_active ?? isActive,
      },
    }).unwrap();
  };

  const handleSave = async () => {
    try {
      const res = await persistMeta();
      if (res.status) { showMessage("Saved"); refetch(); }
    } catch { showErrorMessage("Failed to save"); }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await persistMeta({ is_active: true });
      if (res.status) {
        setIsActive(true);
        showMessage("Published — devices will pick up the new version");
        refetch();
      }
    } catch { showErrorMessage("Failed to publish"); }
    finally { setPublishing(false); }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const next = [...items];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setItems(next); // optimistic
    try {
      const res = await reorderItems({
        id: playlistId, item_ids: next.map(i => i.id),
      }).unwrap();
      if (res.status) { showMessage("Order saved"); refetch(); }
    } catch {
      showErrorMessage("Failed to save order");
      refetch();
    }
  };

  const handleAddBatch = async (mediaItems: IMedia[]) => {
    try {
      for (const m of mediaItems) {
        await addItem({
          id: playlistId,
          media_id: m.id,
          duration: m.media_type === "image" ? DEFAULT_IMAGE_DURATION : null,
        }).unwrap();
      }
      showMessage(`Added ${mediaItems.length} item${mediaItems.length > 1 ? "s" : ""}`);
      refetch();
    } catch { showErrorMessage("Failed to add some items"); }
  };

  const handleRemove = async (itemId: number) => {
    try {
      const res = await removeItem({ playlistId, itemId }).unwrap();
      if (res.status) { showMessage("Item removed"); refetch(); }
    } catch { showErrorMessage("Failed to remove"); }
  };

  const handleSetDuration = async (item: IPlaylistItem, seconds: number) => {
    // duration lives on playlist_items; we re-add via a temporary remove+add to keep
    // the API surface tight. For Phase 5 we can add a PATCH /items/{id} endpoint.
    try {
      await removeItem({ playlistId, itemId: item.id }).unwrap();
      await addItem({
        id: playlistId, media_id: item.media_id, duration: seconds,
      }).unwrap();
      showMessage(`Duration set to ${seconds}s`);
      setDurationFor(null);
      refetch();
    } catch { showErrorMessage("Failed to update duration"); }
  };

  if (isLoading || !playlist) return <PageLoader height="20vh" />;

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* ─── Premium dark header ─── */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.18)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
          <IconButton onClick={() => navigate("/playlist/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
            boxShadow: "0 4px 12px rgba(124,58,237,0.45)",
          }}>
            <PlaylistPlayIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Typography fontSize={20} fontWeight={800} color="#fff" letterSpacing="-0.02em"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 }}>
                {name || "Untitled Playlist"}
              </Typography>
              <Chip size="small" label={`Version ${playlist.version}`}
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: "rgba(212,175,55,0.15)", color: GOLD_SOFT,
                  border: `1px solid ${alpha(GOLD, 0.35)}`,
                }} />
              <Chip size="small"
                label={isActive ? "Active" : "Inactive"}
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: isActive ? "rgba(16,185,129,0.18)" : "rgba(148,163,184,0.18)",
                  color: isActive ? "#a7f3d0" : "#cbd5e1",
                  border: `1px solid ${isActive ? "rgba(16,185,129,0.35)" : "rgba(148,163,184,0.3)"}`,
                }} />
            </Box>
            <Box sx={{ display: "flex", gap: 2.5, mt: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <PermMediaIcon sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }} />
                <Typography fontSize={11.5} color="rgba(255,255,255,0.65)">
                  {items.length} Media Item{items.length === 1 ? "" : "s"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <TvIcon sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }} />
                <Typography fontSize={11.5} color="rgba(255,255,255,0.65)">
                  {playlist.devices_count ?? 0} Device{(playlist.devices_count ?? 0) === 1 ? "" : "s"} Assigned
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }} />
                <Typography fontSize={11.5} color="rgba(255,255,255,0.65)">
                  {formatDuration(stats.imageDuration)} image runtime
                </Typography>
              </Box>
            </Box>
          </Box>
          <Button variant="outlined" size="small"
            startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
            onClick={() => setPreviewOpen(true)} disabled={items.length === 0}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600,
              borderColor: "rgba(255,255,255,0.18)", color: "#e2e8f0", px: 2.5,
              "&:hover": { borderColor: "rgba(255,255,255,0.35)", bgcolor: "rgba(255,255,255,0.08)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.25)", borderColor: "rgba(255,255,255,0.08)" },
            }}>
            Preview
          </Button>
          <Button variant="contained" disableElevation size="small"
            startIcon={isSaving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            onClick={handleSave} disabled={isSaving}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
              color: NAVY_DARK,
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
              boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
              "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
            }}>
            {isSaving ? "Saving…" : "Save Changes"}
          </Button>
          <Button variant="contained" disableElevation size="small"
            startIcon={publishing ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <PublishIcon sx={{ fontSize: 16 }} />}
            onClick={handlePublish} disabled={publishing || items.length === 0}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
              background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
              boxShadow: "0 2px 12px rgba(0,131,143,0.4)",
              "&:hover": { background: "linear-gradient(135deg, #006064 0%, #00838f 100%)" },
              "&.Mui-disabled": { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.25)" },
            }}>
            Publish
          </Button>
        </Box>
      </Box>

      {/* ─── Summary stats ─── */}
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatBadge label="Total Images" value={stats.images}
              icon={<ImageIcon />} tint="#3b82f6" bg="#dbeafe" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatBadge label="Total Videos" value={stats.videos}
              icon={<OndemandVideoIcon />} tint="#0f172a" bg="#e2e8f0" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatBadge label="Image Runtime" value={formatDuration(stats.imageDuration)}
              icon={<TimerIcon />} tint="#f59e0b" bg="#fef3c7" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatBadge label="Assigned Devices" value={playlist.devices_count ?? 0}
              icon={<TvIcon />} tint="#7c3aed" bg="#ede9fe" />
          </Grid>
        </Grid>

        {/* ─── Body: 2-column ─── */}
        <Grid container spacing={3}>
          {/* LEFT: information & summary */}
          <Grid item xs={12} lg={4}>
            <SectionCard
              icon={<InfoOutlinedIcon />}
              title="Playlist Information"
              accent="#3b82f6"
              subtitle="Name, description, status"
            >
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.75, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Playlist Name *
              </Typography>
              <TextField fullWidth value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5, fontSize: 15, fontWeight: 600, bgcolor: "#fff",
                    "& fieldset": { borderColor: "#e2e8f0" },
                    "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 2 },
                  },
                }} />

              <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.75, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Description
              </Typography>
              <TextField fullWidth size="small" multiline rows={3}
                placeholder="Optional — describe where this plays and when…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5, fontSize: 13, bgcolor: "#fff",
                    "& fieldset": { borderColor: "#e2e8f0" },
                    "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 2 },
                  },
                }} />

              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                p: 2, mb: 0.5, borderRadius: 2.5,
                bgcolor: isActive ? alpha("#10b981", 0.05) : "#f8fafc",
                border: `1px solid ${isActive ? alpha("#10b981", 0.2) : "#e2e8f0"}`,
              }}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>Active</Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>
                    Devices only receive active playlists
                  </Typography>
                </Box>
                <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              </Box>
            </SectionCard>

            <SectionCard
              icon={<StorageIcon />}
              title="Library Summary"
              accent="#0e7c9b"
            >
              <InfoRow label="Playlist Version" value={
                <Chip size="small" label={`v${playlist.version}`}
                  sx={{
                    height: 22, fontSize: 11, fontWeight: 800,
                    bgcolor: alpha(GOLD, 0.12), color: "#b58a13",
                    border: `1px solid ${alpha(GOLD, 0.35)}`,
                  }} />
              } />
              <InfoRow label="Total Items" value={items.length} />
              <InfoRow label="Images" value={`${stats.images}`} />
              <InfoRow label="Videos" value={`${stats.videos}`} />
              <InfoRow label="Estimated Runtime" value={
                <Tooltip title="Sum of image durations. Videos play to their natural end.">
                  <span>{formatDuration(stats.imageDuration)} <span style={{ color: "#94a3b8", fontWeight: 400 }}>+ videos</span></span>
                </Tooltip>
              } />
              <InfoRow label="Assigned Devices" value={playlist.devices_count ?? 0} />
            </SectionCard>

            <SectionCard
              icon={<HistoryIcon />}
              title="Timestamps"
              accent="#64748b"
            >
              <InfoRow label="Created" value={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <EventIcon sx={{ fontSize: 13, color: TEXT_SECONDARY }} />
                  {formatDate(playlist.created_at)}
                </Box>
              } />
              <InfoRow label="Last Updated" value={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <AccessTimeIcon sx={{ fontSize: 13, color: TEXT_SECONDARY }} />
                  {formatRelative(playlist.updated_at)}
                </Box>
              } />
            </SectionCard>
          </Grid>

          {/* RIGHT: items */}
          <Grid item xs={12} lg={8}>
            <SectionCard
              icon={<PermMediaIcon />}
              title={`Playlist Items (${items.length})`}
              accent="#7c3aed"
              subtitle="Drag to reorder · click duration to change image timing"
              right={
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="outlined" size="small"
                    startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setPreviewOpen(true)} disabled={items.length === 0}
                    sx={{
                      borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600,
                      borderColor: BORDER, color: TEXT_PRIMARY,
                      "&:hover": { borderColor: "#7c3aed", color: "#7c3aed", bgcolor: alpha("#7c3aed", 0.04) },
                    }}>
                    Preview
                  </Button>
                  <Button variant="contained" disableElevation size="small"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setPickerOpen(true)}
                    sx={{
                      borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
                      background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
                      "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
                    }}>
                    Add Media
                  </Button>
                </Box>
              }
            >
              {items.length === 0 ? (
                <EmptyState
                  onAdd={() => setPickerOpen(true)}
                  onUpload={() => navigate("/media/library")}
                />
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="playlist-items">
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.droppableProps}>
                        {items.map((item, index) => (
                          <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                            {(p, snap) => (
                              <Box ref={p.innerRef} {...p.draggableProps}
                                sx={{
                                  display: "flex", alignItems: "center", gap: 2, p: 1.5, mb: 1.25,
                                  borderRadius: 2.5,
                                  bgcolor: snap.isDragging ? alpha("#7c3aed", 0.08) : "#fff",
                                  border: `1px solid ${snap.isDragging ? alpha("#7c3aed", 0.4) : BORDER}`,
                                  boxShadow: snap.isDragging
                                    ? `0 12px 28px ${alpha("#7c3aed", 0.22)}`
                                    : "0 1px 2px rgba(11,30,45,0.04)",
                                  transition: "all 0.15s ease",
                                  "&:hover": {
                                    borderColor: alpha("#7c3aed", 0.5),
                                    boxShadow: `0 4px 12px ${alpha(NAVY, 0.08)}`,
                                  },
                                }}>
                                <Box {...p.dragHandleProps}
                                  sx={{
                                    cursor: "grab", color: "#cbd5e1",
                                    "&:active": { cursor: "grabbing" },
                                    "&:hover": { color: "#7c3aed" },
                                  }}>
                                  <DragIndicatorIcon />
                                </Box>
                                <Box sx={{
                                  width: 36, height: 36, borderRadius: "10px",
                                  background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.15)} 0%, ${alpha("#7c3aed", 0.05)} 100%)`,
                                  border: `1px solid ${alpha("#7c3aed", 0.2)}`,
                                  color: "#7c3aed",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: 13, fontWeight: 800,
                                }}>
                                  {String(index + 1).padStart(2, "0")}
                                </Box>
                                {item.media && <MediaThumb media={item.media} size={104} />}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{
                                    fontSize: 14, fontWeight: 700, color: NAVY,
                                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                  }}>
                                    {item.media?.original_name || "—"}
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", mt: 0.75 }}>
                                    {item.media && <TypeBadge type={item.media.media_type} />}
                                    {item.media?.media_type === "image" ? (
                                      <Chip size="small"
                                        icon={<TimerIcon sx={{ fontSize: 12 }} />}
                                        label={`${item.duration || DEFAULT_IMAGE_DURATION}s`}
                                        onClick={() => setDurationFor(item)}
                                        sx={{
                                          height: 22, fontSize: 11, fontWeight: 700, cursor: "pointer",
                                          bgcolor: "#fef3c7", color: "#92400e",
                                          border: "1px solid #fcd34d",
                                          "& .MuiChip-icon": { color: "#d97706", ml: 0.5 },
                                          "&:hover": { bgcolor: "#fde68a" },
                                        }} />
                                    ) : (
                                      <Chip size="small"
                                        icon={<PlayArrowIcon sx={{ fontSize: 12 }} />}
                                        label="plays to end"
                                        sx={{
                                          height: 22, fontSize: 11, fontWeight: 700,
                                          bgcolor: "#e0e7ff", color: "#3730a3",
                                          border: "1px solid #c7d2fe",
                                          "& .MuiChip-icon": { color: "#4f46e5", ml: 0.5 },
                                        }} />
                                    )}
                                    {item.media?.file_size != null && (
                                      <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                                        {formatBytes(item.media.file_size)}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <Box sx={{ display: "flex", gap: 0.5 }}>
                                  <Tooltip title="Preview from this item">
                                    <IconButton size="small"
                                      onClick={() => setPreviewOpen(true)}
                                      sx={{
                                        color: GOLD,
                                        "&:hover": { bgcolor: alpha(GOLD, 0.08) },
                                      }}>
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {item.media?.media_type === "image" && (
                                    <Tooltip title="Edit display duration">
                                      <IconButton size="small"
                                        onClick={() => setDurationFor(item)}
                                        sx={{
                                          color: "#f59e0b",
                                          "&:hover": { bgcolor: "#fef3c7" },
                                        }}>
                                        <TimerIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Remove from playlist">
                                    <IconButton size="small"
                                      onClick={() => handleRemove(item.id)}
                                      sx={{
                                        color: "#94a3b8",
                                        "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" },
                                      }}>
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
              {isReordering && (
                <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 1, textAlign: "right" }}>
                  Saving order…
                </Typography>
              )}
            </SectionCard>
          </Grid>
        </Grid>
      </Box>

      {/* ─── Modals ─── */}
      <AddMediaDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={async (m) => { await handleAddBatch(m); setPickerOpen(false); }}
      />
      <PreviewDialog
        open={previewOpen}
        items={items}
        onClose={() => setPreviewOpen(false)}
        playlistName={name}
      />
      <DurationDialog
        open={!!durationFor}
        currentDuration={durationFor?.duration ?? DEFAULT_IMAGE_DURATION}
        onClose={() => setDurationFor(null)}
        onConfirm={(s) => durationFor && handleSetDuration(durationFor, s)}
      />
    </Box>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Empty state                                                                */
/* ──────────────────────────────────────────────────────────────────────── */

const EmptyState: React.FC<{ onAdd: () => void; onUpload: () => void }> = ({ onAdd, onUpload }) => (
  <Box sx={{
    py: 7, px: 3, textAlign: "center",
    border: `2px dashed ${BORDER}`, borderRadius: 3, bgcolor: "#f8fafc",
  }}>
    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
      <svg width="160" height="100" viewBox="0 0 160 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="screen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e293b" />
            <stop offset="100%" stopColor="#0f172a" />
          </linearGradient>
          <linearGradient id="gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#D4AF37" />
            <stop offset="100%" stopColor="#F5D97E" />
          </linearGradient>
        </defs>
        <rect x="20" y="14" width="120" height="70" rx="7" fill="url(#screen)" />
        <rect x="26" y="20" width="108" height="58" rx="3" fill="#0b1220" />
        <circle cx="80" cy="49" r="14" fill="url(#gold)" />
        <polygon points="76,42 76,56 89,49" fill="#0f172a" />
        <rect x="68" y="86" width="24" height="3" rx="1.5" fill="#334155" />
        <rect x="58" y="91" width="44" height="5" rx="2.5" fill="#334155" />
        <circle cx="36" cy="32" r="2.5" fill="#7c3aed" />
        <circle cx="124" cy="32" r="2.5" fill="#3b82f6" />
        <circle cx="36" cy="66" r="2.5" fill="#10b981" />
        <circle cx="124" cy="66" r="2.5" fill="#ef4444" />
      </svg>
    </Box>
    <Typography sx={{ fontSize: 17, fontWeight: 800, color: NAVY, mb: 0.5, letterSpacing: "-0.01em" }}>
      No media added yet
    </Typography>
    <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 3, maxWidth: 400, mx: "auto" }}>
      Add images and videos from your library, or upload new files to start building this playlist.
    </Typography>
    <Box sx={{ display: "flex", gap: 1.25, justifyContent: "center" }}>
      <Button variant="contained" disableElevation
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onClick={onAdd}
        sx={{
          borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, px: 3,
          background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
          "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
        }}>
        Add Media
      </Button>
      <Button variant="outlined"
        startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
        onClick={onUpload}
        sx={{
          borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, px: 3,
          borderColor: BORDER, color: NAVY,
          "&:hover": { borderColor: NAVY, bgcolor: alpha(NAVY, 0.04) },
        }}>
        Upload Media
      </Button>
    </Box>
  </Box>
);

/* ──────────────────────────────────────────────────────────────────────── */
/* Full-screen Add Media modal                                                */
/* ──────────────────────────────────────────────────────────────────────── */

const AddMediaDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: (media: IMedia[]) => Promise<void>;
}> = ({ open, onClose, onConfirm }) => {
  const [search, setSearch] = useState("");
  const [type, setType] = useState<"" | "image" | "video">("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { showMessage, showErrorMessage } = useNotifier();
  const { enqueue, hasActive } = useUploads();

  const { data, refetch } = useGetMediaQuery({ searchText: search, type: type || undefined });
  const media: IMedia[] = useMemo(
    () => Array.isArray(data?.data) ? ((data?.data as unknown) as IMedia[]) : [],
    [data]
  );

  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setSearch("");
      setType("");
    }
  }, [open]);

  const toggle = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selected = media.filter(m => selectedIds.has(m.id));

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const results = await Promise.allSettled(
      Array.from(files).map(f => enqueue(f))
    );
    const ok = results.filter(r => r.status === "fulfilled").length;
    const failed = results.filter(r => r.status === "rejected");
    if (ok > 0) {
      showMessage(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`);
      refetch();
    }
    failed.forEach((r: any) => {
      const msg = r.reason?.message || "Upload failed";
      if (msg && !msg.toLowerCase().includes("cancel")) showErrorMessage(msg);
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen
      PaperProps={{ sx: { bgcolor: "#f1f5f9" } }}>
      {/* Dark header */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, py: 2, display: "flex", alignItems: "center", gap: 2,
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
        }}>
          <PermMediaIcon sx={{ fontSize: 18, color: "#fff" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={17} fontWeight={800} color="#fff">
            Add Media to Playlist
          </Typography>
          <Typography fontSize={11} color="rgba(255,255,255,0.55)">
            Select media from your library or upload new files
          </Typography>
        </Box>
        <Chip size="small"
          label={`${selectedIds.size} selected`}
          sx={{
            height: 26, fontSize: 12, fontWeight: 700,
            bgcolor: selectedIds.size > 0 ? "rgba(212,175,55,0.18)" : "rgba(255,255,255,0.08)",
            color: selectedIds.size > 0 ? GOLD_SOFT : "rgba(255,255,255,0.55)",
            border: `1px solid ${selectedIds.size > 0 ? alpha(GOLD, 0.4) : "rgba(255,255,255,0.12)"}`,
            px: 1,
          }} />
        <Button variant="outlined" size="small" onClick={onClose}
          sx={{
            borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600,
            borderColor: "rgba(255,255,255,0.18)", color: "#e2e8f0", px: 2.5,
            "&:hover": { borderColor: "rgba(255,255,255,0.35)", bgcolor: "rgba(255,255,255,0.06)" },
          }}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation size="small"
          disabled={selectedIds.size === 0}
          onClick={() => onConfirm(selected)}
          startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
          sx={{
            borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
            color: NAVY_DARK,
            background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
            boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
            "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
            "&.Mui-disabled": { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.25)" },
          }}>
          Add to Playlist
        </Button>
      </Box>

      <DialogContent sx={{ p: 0, display: "flex", overflow: "hidden" }}>
        {/* Library */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, alignItems: "center" }}>
            <TextField size="small" placeholder="Search media…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
              }}
              sx={{
                width: 320,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2, fontSize: 13, bgcolor: "#fff",
                  "& fieldset": { borderColor: BORDER },
                  "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: 1.5 },
                },
              }} />
            {[
              { v: "", label: "All", icon: <PermMediaIcon sx={{ fontSize: 14 }} /> },
              { v: "image", label: "Images", icon: <ImageIcon sx={{ fontSize: 14 }} /> },
              { v: "video", label: "Videos", icon: <OndemandVideoIcon sx={{ fontSize: 14 }} /> },
            ].map(opt => (
              <Button key={opt.v} size="small" startIcon={opt.icon}
                onClick={() => setType(opt.v as any)}
                sx={{
                  textTransform: "none", fontSize: 12, fontWeight: 600,
                  borderRadius: 1.5, px: 1.75, border: "1px solid",
                  borderColor: type === opt.v ? GOLD : BORDER,
                  bgcolor: type === opt.v ? alpha(GOLD, 0.08) : "#fff",
                  color: type === opt.v ? NAVY : TEXT_SECONDARY,
                  "&:hover": { borderColor: GOLD, color: NAVY, bgcolor: alpha(GOLD, 0.08) },
                }}>
                {opt.label}
              </Button>
            ))}
            <Box sx={{ flex: 1 }} />
            {media.length > 0 && (() => {
              const allSelected = media.every(m => selectedIds.has(m.id));
              return (
                <Button size="small" variant="outlined"
                  startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    setSelectedIds(prev => {
                      if (allSelected) {
                        const next = new Set(prev);
                        media.forEach(m => next.delete(m.id));
                        return next;
                      }
                      const next = new Set(prev);
                      media.forEach(m => next.add(m.id));
                      return next;
                    });
                  }}
                  sx={{
                    borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600,
                    borderColor: allSelected ? "#7c3aed" : BORDER,
                    color: allSelected ? "#7c3aed" : TEXT_PRIMARY,
                    bgcolor: allSelected ? alpha("#7c3aed", 0.06) : "#fff",
                    "&:hover": { borderColor: "#7c3aed", bgcolor: alpha("#7c3aed", 0.08), color: "#7c3aed" },
                  }}>
                  {allSelected ? `Deselect All (${media.length})` : `Select All (${media.length})`}
                </Button>
              );
            })()}
            <input ref={fileInputRef} type="file" multiple hidden
              accept=".jpg,.jpeg,.png,.webp,.mp4"
              onChange={(e) => handleUpload(e.target.files)} />
            <Button size="small" variant="outlined"
              startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600,
                borderColor: BORDER, color: TEXT_PRIMARY,
                "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.08), color: NAVY },
              }}>
              {hasActive ? "Uploading…" : "Upload"}
            </Button>
          </Box>

          {media.length === 0 ? (
            <Box sx={{
              py: 8, textAlign: "center", border: `2px dashed ${BORDER}`,
              borderRadius: 3, bgcolor: "#fff",
            }}>
              <PermMediaIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1.5 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY, mb: 0.5 }}>
                No media in your library
              </Typography>
              <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
                Upload images or videos to get started.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {media.map(m => {
                const isSel = selectedIds.has(m.id);
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2.4} key={m.id}>
                    <Paper elevation={0} onClick={() => toggle(m.id)}
                      sx={{
                        position: "relative", cursor: "pointer",
                        borderRadius: 3, overflow: "hidden", bgcolor: "#fff",
                        border: `2px solid ${isSel ? "#7c3aed" : BORDER}`,
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: isSel ? "#7c3aed" : alpha("#7c3aed", 0.4),
                          transform: "translateY(-2px)",
                          boxShadow: `0 8px 22px ${alpha(NAVY, 0.1)}`,
                        },
                      }}>
                      <Box sx={{ position: "relative" }}>
                        {m.media_type === "image" ? (
                          <Box component="img" src={m.url} alt=""
                            sx={{ width: "100%", height: 130, objectFit: "cover", display: "block", bgcolor: "#f1f5f9" }} />
                        ) : (
                          <Box sx={{
                            height: 130,
                            background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#fff",
                          }}>
                            <PlayCircleFilledIcon sx={{ fontSize: 44, opacity: 0.95 }} />
                          </Box>
                        )}
                        <Box sx={{
                          position: "absolute", top: 8, left: 8, zIndex: 1,
                        }}>
                          <TypeBadge type={m.media_type} />
                        </Box>
                        <Box sx={{
                          position: "absolute", top: 8, right: 8,
                          width: 24, height: 24, borderRadius: "50%",
                          border: `2px solid ${isSel ? "#7c3aed" : "rgba(255,255,255,0.85)"}`,
                          bgcolor: isSel ? "#7c3aed" : "rgba(15,23,42,0.55)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "all 0.15s ease",
                          boxShadow: isSel ? "0 2px 8px rgba(124,58,237,0.45)" : "none",
                        }}>
                          {isSel && <CheckCircleIcon sx={{ fontSize: 18, color: "#fff" }} />}
                        </Box>
                      </Box>
                      <Box sx={{ p: 1.25 }}>
                        <Typography sx={{
                          fontSize: 12, fontWeight: 700, color: NAVY,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}>
                          {m.original_name}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: TEXT_SECONDARY, mt: 0.25 }}>
                          {formatBytes(m.file_size)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        {/* Selected panel */}
        <Box sx={{
          width: 320, borderLeft: `1px solid ${BORDER}`, bgcolor: "#fff",
          display: "flex", flexDirection: "column",
        }}>
          <Box sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Selected Media
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: NAVY, mt: 0.25, lineHeight: 1.2 }}>
              {selectedIds.size}
            </Typography>
            <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
              {selectedIds.size === 0
                ? "Click items on the left to add them"
                : `${selectedIds.size} item${selectedIds.size === 1 ? "" : "s"} ready to add`}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
            {selected.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <PermMediaIcon sx={{ fontSize: 32, color: "#cbd5e1", mb: 1 }} />
                <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
                  Nothing selected yet
                </Typography>
              </Box>
            ) : selected.map(m => (
              <Box key={m.id} sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                p: 1, mb: 0.5, borderRadius: 2,
                "&:hover": { bgcolor: "#f8fafc" },
              }}>
                <MediaThumb media={m} size={56} rounded={6} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: 12, fontWeight: 700, color: NAVY,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {m.original_name}
                  </Typography>
                  <TypeBadge type={m.media_type} />
                </Box>
                <IconButton size="small" onClick={() => toggle(m.id)}
                  sx={{ color: "#94a3b8", "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" } }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
          <Box sx={{ p: 2, borderTop: `1px solid ${BORDER}` }}>
            <Button fullWidth variant="contained" disableElevation
              disabled={selectedIds.size === 0}
              onClick={() => onConfirm(selected)}
              startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, py: 1.25,
                color: NAVY_DARK,
                background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
                "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
                "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8" },
              }}>
              Add {selectedIds.size > 0 ? `${selectedIds.size} ` : ""}to Playlist
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Preview dialog — simulates playback                                       */
/* ──────────────────────────────────────────────────────────────────────── */

const PreviewDialog: React.FC<{
  open: boolean;
  items: IPlaylistItem[];
  onClose: () => void;
  playlistName: string;
}> = ({ open, items, onClose, playlistName }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => { if (open) { setIndex(0); setProgress(0); setPaused(false); } }, [open]);
  const current = items[index];

  useEffect(() => {
    if (!open || !current || paused) return;
    if (current.media?.media_type === "video") return; // video uses its own ended event
    const duration = (current.duration || DEFAULT_IMAGE_DURATION) * 1000;
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / duration) * 100);
      setProgress(pct);
      if (elapsed >= duration) {
        setIndex(i => (i + 1) % items.length);
        setProgress(0);
        clearInterval(tick);
      }
    }, 100);
    return () => clearInterval(tick);
  }, [open, index, current, paused, items.length]);

  const next = () => { setIndex(i => (i + 1) % items.length); setProgress(0); };

  if (!open || !current) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", bgcolor: "#0f172a" } }}>
      <Box sx={{ position: "relative" }}>
        <Box sx={{ position: "relative", height: "70vh", bgcolor: "#000" }}>
          {current.media?.media_type === "image" ? (
            <Box component="img" src={current.media?.url} alt=""
              sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
          ) : (
            <Box component="video" ref={videoRef as any}
              src={current.media?.url} autoPlay muted={false} controls={false}
              onEnded={next}
              sx={{ width: "100%", height: "100%", display: "block", objectFit: "contain", bgcolor: "#000" }} />
          )}
          <IconButton onClick={onClose}
            sx={{
              position: "absolute", top: 12, right: 12, color: "#fff",
              bgcolor: "rgba(0,0,0,0.55)", backdropFilter: "blur(10px)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
            }}>
            <CloseIcon />
          </IconButton>
          <Box sx={{
            position: "absolute", top: 12, left: 12,
            display: "flex", gap: 1, alignItems: "center",
            bgcolor: "rgba(0,0,0,0.55)", backdropFilter: "blur(10px)",
            color: "#fff", px: 1.5, py: 0.5, borderRadius: 1.5,
          }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em" }}>
              PREVIEW
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
              {playlistName}
            </Typography>
          </Box>
        </Box>

        {/* Progress bar (image only — video has its own duration) */}
        <Box sx={{ height: 4, bgcolor: "#1e293b" }}>
          <Box sx={{
            height: "100%",
            background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
            width: current.media?.media_type === "image" ? `${progress}%` : "100%",
            transition: current.media?.media_type === "image" ? "width 100ms linear" : "none",
          }} />
        </Box>

        {/* Bottom controls / item list */}
        <Box sx={{
          background: "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)",
          color: "#fff", p: 2,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
            <IconButton onClick={() => setPaused(p => !p)}
              sx={{
                color: NAVY_DARK,
                bgcolor: GOLD,
                "&:hover": { bgcolor: GOLD_SOFT },
              }}>
              {paused ? <PlayArrowIcon /> : <PauseIcon />}
            </IconButton>
            <IconButton onClick={next}
              sx={{ color: "#cbd5e1", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.06)" } }}>
              <SkipNextIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {current.media?.original_name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
                Item {index + 1} of {items.length} ·{" "}
                {current.media?.media_type === "image"
                  ? `${current.duration || DEFAULT_IMAGE_DURATION}s display`
                  : "plays to end"}
              </Typography>
            </Box>
            {current.media && <TypeBadge type={current.media.media_type} />}
          </Box>

          {/* Step dots */}
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
            {items.map((it, i) => (
              <Tooltip key={it.id} title={it.media?.original_name ?? ""}>
                <Box onClick={() => { setIndex(i); setProgress(0); }}
                  sx={{
                    width: i === index ? 24 : 8, height: 8, borderRadius: 4,
                    bgcolor: i === index ? GOLD : i < index ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.18)",
                    cursor: "pointer", transition: "all 0.2s ease",
                  }} />
              </Tooltip>
            ))}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Duration dialog                                                            */
/* ──────────────────────────────────────────────────────────────────────── */

const DurationDialog: React.FC<{
  open: boolean;
  currentDuration: number;
  onClose: () => void;
  onConfirm: (s: number) => void;
}> = ({ open, currentDuration, onClose, onConfirm }) => {
  const [custom, setCustom] = useState<string>(String(currentDuration));

  useEffect(() => { if (open) setCustom(String(currentDuration)); }, [open, currentDuration]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: NAVY, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 2,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
          }}>
            <TimerIcon sx={{ fontSize: 16, color: "#d97706" }} />
          </Box>
          Display Duration
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mb: 1.5 }}>
          How long should this image stay on screen?
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {DURATION_PRESETS.map(p => (
            <Button key={p} size="small"
              onClick={() => onConfirm(p)}
              sx={{
                textTransform: "none", fontSize: 13, fontWeight: 700,
                borderRadius: 2, px: 2, py: 0.75, minWidth: 64,
                border: "1px solid",
                borderColor: p === currentDuration ? GOLD : BORDER,
                bgcolor: p === currentDuration ? alpha(GOLD, 0.08) : "#fff",
                color: p === currentDuration ? NAVY : TEXT_PRIMARY,
                "&:hover": { borderColor: GOLD, color: NAVY, bgcolor: alpha(GOLD, 0.08) },
              }}>
              {p}s
            </Button>
          ))}
        </Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Custom (seconds)
        </Typography>
        <TextField fullWidth size="small" type="number"
          inputProps={{ min: 1, max: 3600 }}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5, fontSize: 14, bgcolor: "#fff",
              "& fieldset": { borderColor: "#e2e8f0" },
              "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: 2 },
            },
          }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose}
          sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#64748b" }}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation
          onClick={() => {
            const n = Math.max(1, Math.min(3600, parseInt(custom, 10) || DEFAULT_IMAGE_DURATION));
            onConfirm(n);
          }}
          sx={{
            borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
            color: NAVY_DARK,
            background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
            "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
          }}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaylistEdit;
